/**
 * Draws the base layer of a picture with WebGL 2 (the graphics card): every shape in **one**
 * instanced draw call, in list order, so they overlap exactly as on Canvas 2D.
 *
 * {@link GlPainter.create} returns `null` when the page cannot use the graphics card (no
 * WebGL 2, or the shaders do not compile), and the caller draws with Canvas 2D instead. The
 * browser may also take the card back at any time (driver reset, too many pages using it): the
 * painter then reports {@link GlPainter.lost} and the caller switches to Canvas 2D too.
 */
import type { FlatShape } from "../shapes";
import { browserColors, type ColorReader } from "./colors";
import { isSoftwareDevice } from "./device";
import { FLOATS, packShapes } from "./instances";
import { FRAGMENT_SHADER, VERTEX_SHADER } from "./shaders";

/** Draws shapes on a canvas with WebGL 2. */
export class GlPainter {
  /** Set when the browser took the graphics card back: nothing can be drawn any more. */
  lost = false;
  /** The instance numbers, reused from frame to frame. */
  private data: Float32Array<ArrayBuffer> = new Float32Array(0);
  /** The size the instance buffer was given on the card, in bytes. */
  private bufferBytes = 0;
  /** The size of the canvas in CSS pixels, and the device pixels per CSS pixel. */
  private size = { width: 1, height: 1, dpr: 1 };

  /** Sets everything up; private: use {@link GlPainter.create}. */
  private constructor(
    /** The canvas drawn on. */
    readonly canvas: HTMLCanvasElement,
    /** Its WebGL 2 context. */
    private readonly gl: WebGL2RenderingContext,
    /** The compiled shaders. */
    private readonly program: WebGLProgram,
    /** The vertex array: which buffer feeds which shader input. */
    private readonly vao: WebGLVertexArrayObject,
    /** The buffer of instance numbers. */
    private readonly instances: WebGLBuffer,
    /** Where the canvas size goes in the vertex shader. */
    private readonly sizeAt: WebGLUniformLocation | null,
    /** Where the softening width goes in the fragment shader. */
    private readonly softAt: WebGLUniformLocation | null,
    /** Turns CSS colours into numbers. */
    private readonly color: ColorReader,
    /** The graphics card's name, as the browser tells it (for the log). */
    readonly device: string,
  ) {
    canvas.addEventListener("webglcontextlost", this.onLost);
  }

  /**
   * The painter, or `null` (with why) when the graphics card cannot be used. A graphics card the
   * browser only emulates on the processor is refused (slower than Canvas 2D), unless
   * `allowSoftware` (browser checks, whose headless browser has no other).
   */
  static create(canvas: HTMLCanvasElement, allowSoftware = false): { painter: GlPainter } | { painter: null; why: string } {
    let gl: WebGL2RenderingContext | null = null;
    try {
      // The shader softens the edges itself; the browser's anti-aliasing would only cost time.
      gl = canvas.getContext("webgl2", { antialias: false, premultipliedAlpha: true, alpha: true, depth: false, stencil: false, powerPreference: "high-performance", failIfMajorPerformanceCaveat: !allowSoftware });
    } catch {
      gl = null;
    }
    if (!gl) return { painter: null, why: allowSoftware ? "the browser offers no WebGL 2" : "the browser offers no WebGL 2 on a real graphics card" };
    const info = gl.getExtension("WEBGL_debug_renderer_info");
    const device = String(info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
    if (!allowSoftware && isSoftwareDevice(device)) {
      gl.getExtension("WEBGL_lose_context")?.loseContext();
      return { painter: null, why: `the graphics card is only emulated (${device})` };
    }
    const program = link(gl);
    if (typeof program === "string") return { painter: null, why: program };

    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    // The square's four corners, shared by every instance.
    const corners = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, corners);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    // One instance per shape: see the table in `instances.ts`.
    const instances = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, instances);
    const stride = FLOATS * 4;
    for (const [location, size, offset] of [
      [1, 4, 0],
      [2, 4, 4],
      [3, 2, 8],
      [4, 4, 10],
      [5, 4, 14],
    ] as const) {
      gl.enableVertexAttribArray(location);
      gl.vertexAttribPointer(location, size, gl.FLOAT, false, stride, offset * 4);
      gl.vertexAttribDivisor(location, 1);
    }
    gl.bindVertexArray(null);

    const painter = new GlPainter(canvas, gl, program, vao, instances, gl.getUniformLocation(program, "u_size"), gl.getUniformLocation(program, "u_soft"), browserColors(), device);
    return { painter };
  }

  /** Follows the canvas's size (CSS pixels) and the screen's pixel density. */
  resize(width: number, height: number, dpr: number): void {
    this.size = { width, height, dpr };
    this.canvas.width = Math.round(width * dpr);
    this.canvas.height = Math.round(height * dpr);
  }

  /** Clears the canvas and draws `shapes`, in order. */
  paint(shapes: readonly FlatShape[]): void {
    if (this.lost) return;
    const gl = this.gl;
    const packed = packShapes(shapes, this.color, this.data);
    this.data = packed.data;
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (!packed.count) return;
    gl.useProgram(this.program);
    gl.uniform2f(this.sizeAt, this.size.width, this.size.height);
    gl.uniform1f(this.softAt, 1 / this.size.dpr);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instances);
    // Grow the card's buffer only when needed; otherwise only replace its start.
    const bytes = this.data.byteLength;
    if (bytes > this.bufferBytes) {
      gl.bufferData(gl.ARRAY_BUFFER, bytes, gl.DYNAMIC_DRAW);
      this.bufferBytes = bytes;
    }
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.data, 0, packed.count * FLOATS);
    gl.bindVertexArray(this.vao);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, packed.count);
    gl.bindVertexArray(null);
  }

  /** Gives the graphics card's memory back. */
  dispose(): void {
    this.canvas.removeEventListener("webglcontextlost", this.onLost);
    if (this.lost) return;
    this.gl.deleteBuffer(this.instances);
    this.gl.deleteVertexArray(this.vao);
    this.gl.deleteProgram(this.program);
    this.gl.getExtension("WEBGL_lose_context")?.loseContext();
  }

  /** The browser took the graphics card back. */
  private onLost = (event: Event) => {
    event.preventDefault();
    this.lost = true;
  };
}

/** Compiles and links the two shaders; a string says why it failed. */
function link(gl: WebGL2RenderingContext): WebGLProgram | string {
  /** Compiles one shader. */
  const compile = (type: number, source: string): WebGLShader | string => {
    const shader = gl.createShader(type);
    if (!shader) return "the graphics card refused a shader";
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return shader;
    const log = gl.getShaderInfoLog(shader) ?? "";
    gl.deleteShader(shader);
    return `a shader did not compile: ${log.trim()}`;
  };
  const vertex = compile(gl.VERTEX_SHADER, VERTEX_SHADER);
  if (typeof vertex === "string") return vertex;
  const fragment = compile(gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
  if (typeof fragment === "string") return fragment;
  const program = gl.createProgram();
  if (!program) return "the graphics card refused the program";
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  if (gl.getProgramParameter(program, gl.LINK_STATUS)) return program;
  return `the shaders did not link: ${(gl.getProgramInfoLog(program) ?? "").trim()}`;
}
